// Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved.

import { postPasswordStrengthAsync } from '@/api/userApi';
import i18n from '@/i18n';
import { FieldLengths } from '@/models/constants';
import { ProfileInfo } from '@/models/models';
import { isError } from '@/utils/result';
import { computed } from 'vue';

export const passwordTooShort = {
    validationFunction: (password: string | null) => {
        if (!password) {
            return true;
        }

        return password.length >= FieldLengths.Password.min;
    },
    errorMessageText: computed(() => i18n.global.t('signUp.passwordTooShort')),
};

export const passwordHasProfileData = (userProfile: ProfileInfo) => ({
    validationFunction: (password: string | null) => {
        if (!password) {
            return true;
        }

        const value = password.toLowerCase();
        return (
            (!userProfile.companyName || !value.includes(userProfile.companyName.toLowerCase())) &&
            (!userProfile.firstName || !value.includes(userProfile.firstName.toLowerCase())) &&
            (!userProfile.lastName || !value.includes(userProfile.lastName.toLowerCase())) &&
            (!userProfile.email || !value.includes(userProfile.email.toLowerCase()))
        );
    },
    errorMessageText: computed(() => i18n.global.t('signUp.passwordHasCommonNames')),
});

export const passwordMatches = (newPassword: string) => ({
    validationFunction: (confirmPassword: string | null) => {
        return confirmPassword === newPassword;
    },
    errorMessageText: computed(() => i18n.global.t('auth.claimAccount.matchPassword')),
});

export const checkPasswordValidity = async (userId: string | null, password: string) => {
    try {
        const response = await postPasswordStrengthAsync({
            password: password,
            userId: userId,
        });

        if (isError(response)) {
            return null;
        }

        if (
            response.tooShort ||
            response.hasCommonNames ||
            response.hasRepetitiveOrSequential
        ) {
            if (response.hasCommonNames) {
                return computed(() => i18n.global.t('signUp.passwordHasCommonNames'));
            } else if (response.hasRepetitiveOrSequential) {
                return computed(() => i18n.global.t('signUp.passwordHasRepetitiveOrSequential'));
            } else if (response.tooShort) {
                return computed(() => i18n.global.t('signUp.passwordTooShort'));
            }
        }
    } catch {
        return null;
    }

    return null;
}

export default {
    passwordTooShort,
    passwordHasProfileData,
    passwordMatches,
    checkPasswordValidity,
};
